import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, MessageCircle, Mail, Calendar, Users, Sparkles, Wallet, FileText, CalendarCheck,
  Pencil, TrendingUp, Ban, RotateCcw, FilePlus2, Trash2, AlertTriangle, RefreshCw,
} from "lucide-react";
import Modal from "../components/Modal";
import Badge from "../components/Badge";
import ActionMenu, { type ActionMenuItem } from "../components/ActionMenu";
import { enquiriesApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatDate, formatCurrencyFull } from "@/lib/data";
import {
  enquiryName, enquiryPhone, enquiryEmail, latestQuotation, statusLabel, telHref, whatsappHref,
  type EnquiryData,
} from "./enquiry";

interface EnquiryDetailModalProps {
  isOpen: boolean;
  enquiryId: number | null;
  /** Bump to make the popup load the enquiry again (e.g. after it was edited) */
  refreshKey?: number;
  onClose: () => void;
  onEdit: (enquiry: EnquiryData) => void;
  onCreateQuotation: (enquiry: EnquiryData) => void;
  onDelete: (enquiry: EnquiryData) => void;
  /** Called after the popup changed the enquiry (status), so the list can refresh */
  onChanged?: () => void;
  canQuote: boolean;
  canDelete: boolean;
}

export default function EnquiryDetailModal({
  isOpen,
  enquiryId,
  refreshKey = 0,
  onClose,
  onEdit,
  onCreateQuotation,
  onDelete,
  onChanged,
  canQuote,
  canDelete,
}: EnquiryDetailModalProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [enquiry, setEnquiry] = useState<EnquiryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!enquiryId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await enquiriesApi.get(enquiryId);
      setEnquiry(res.data as EnquiryData);
    } catch (err) {
      setEnquiry(null);
      setError(apiErrorMessage(err, "Couldn't load this enquiry"));
    } finally {
      setLoading(false);
    }
  }, [enquiryId]);

  useEffect(() => {
    if (!isOpen || !enquiryId) {
      setEnquiry(null);
      setError(null);
      return;
    }
    load();
  }, [isOpen, enquiryId, refreshKey, load]);

  const changeStatus = async (status: string) => {
    if (!enquiry) return;
    setSaving(true);
    try {
      const res = await enquiriesApi.update(enquiry.id, { status });
      setEnquiry(res.data as EnquiryData);
      showToast(`Marked as ${statusLabel(status).toLowerCase()}`, "success");
      onChanged?.();
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't change the status"), "error");
    } finally {
      setSaving(false);
    }
  };

  const e = enquiry;
  const name = e ? enquiryName(e) : "Enquiry";
  const phone = e ? enquiryPhone(e) : "";
  const email = e ? enquiryEmail(e) : "";
  const tel = telHref(phone);
  const wa = whatsappHref(phone);
  const quote = e ? latestQuotation(e) : undefined;

  const openQuotation = (id: number) => { onClose(); navigate(`/quotations/detail?id=${id}`); };

  // One main next step: open the quotation if there is one, otherwise make one
  const primary = !e ? null
    : quote && canQuote ? { label: "Open quotation", onClick: () => openQuotation(quote.id) }
    : e.status !== "rejected" && canQuote ? { label: "Create quotation", onClick: () => onCreateQuotation(e) }
    : null;

  const menu: ActionMenuItem[] = [];
  if (e) {
    if (quote && canQuote && e.status !== "rejected") menu.push({ label: "Create another quotation", icon: FilePlus2, onClick: () => onCreateQuotation(e) });
    if (e.status === "new") menu.push({ label: "Mark qualified", icon: TrendingUp, onClick: () => changeStatus("qualified") });
    if (e.status === "new" || e.status === "qualified") menu.push({ label: "Mark rejected", icon: Ban, onClick: () => changeStatus("rejected") });
    if (e.status === "rejected") menu.push({ label: "Reopen", icon: RotateCcw, onClick: () => changeStatus("new") });
    if (canDelete) menu.push({ label: "Delete…", icon: Trash2, danger: true, separated: menu.length > 0, onClick: () => onDelete(e) });
  }

  const row = (Icon: typeof Phone, label: string, value: ReactNode) => (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-stone-400">{label}</p>
        <div className="text-sm text-stone-800 break-words">{value}</div>
      </div>
    </div>
  );

  const footer = e ? (
    <div className="flex items-center gap-2">
      {primary && (
        <button
          type="button"
          onClick={primary.onClick}
          disabled={saving}
          className="flex-1 sm:flex-none h-11 px-5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 disabled:opacity-50"
        >
          {primary.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => onEdit(e)}
        disabled={saving}
        className={`${primary ? "" : "flex-1 sm:flex-none"} h-11 px-4 inline-flex items-center justify-center gap-2 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50`}
      >
        <Pencil className="w-4 h-4" /> Edit
      </button>
      <div className="sm:flex-1" />
      <ActionMenu items={menu} label="More actions for this enquiry" />
    </div>
  ) : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={e ? name : error ? "Enquiry" : "Loading enquiry…"} size="lg" footer={footer}>
      {!e && !error ? (
        <div className="space-y-4" aria-label="Loading enquiry">
          <div className="h-5 w-24 rounded bg-stone-100 animate-pulse" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2"><div className="h-3 w-20 rounded bg-stone-100 animate-pulse" /><div className="h-4 w-2/3 rounded bg-stone-100 animate-pulse" /></div>
          ))}
        </div>
      ) : error || !e ? (
        <div className="text-center py-8">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load this enquiry</p>
          <p className="text-sm text-stone-500 mt-1">{error || "It may have been deleted."}</p>
          <button type="button" onClick={load} disabled={loading} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Try again
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge label={statusLabel(e.status)} status={e.status} />
            <span className="text-xs text-stone-500">
              #{e.id}{e.created_at ? ` · Received ${formatDate(e.created_at)}` : ""}{e.lead_source ? ` · ${e.lead_source}` : ""}
            </span>
          </div>

          {/* Contact */}
          <section>
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Contact</h3>
            <div className="divide-y divide-stone-100">
              {row(Phone, "Phone", phone || "Not given")}
              {row(Mail, "Email", email ? <a href={`mailto:${email}`} className="text-amber-700 hover:underline">{email}</a> : "Not given")}
            </div>
            {(tel || wa) && (
              <div className="flex gap-2 mt-2">
                {tel && (
                  <a href={tel} className="flex-1 sm:flex-none h-10 px-4 inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
                {wa && (
                  <a href={wa} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none h-10 px-4 inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                )}
              </div>
            )}
          </section>

          {/* Event */}
          <section>
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Event</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 divide-y sm:divide-y-0 divide-stone-100">
              {row(Sparkles, "Type", e.type || "Not given")}
              {row(Calendar, "Date", e.event_date ? formatDate(e.event_date) : "Not given")}
              {row(Users, "Guests", e.guest_count ? e.guest_count.toLocaleString("en-IN") : "Not given")}
              {row(Wallet, "Budget", Number(e.budget) > 0 ? formatCurrencyFull(Number(e.budget)) : "Not given")}
            </div>
          </section>

          {e.notes && (
            <section>
              <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">Notes</h3>
              <p className="text-sm text-stone-700 whitespace-pre-wrap bg-stone-50 rounded-lg p-3">{e.notes}</p>
            </section>
          )}

          {/* What came of it */}
          <section>
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Quotations</h3>
            {e.quotations && e.quotations.length > 0 ? (
              <div className="space-y-2">
                {e.quotations.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => openQuotation(q.id)}
                    disabled={!canQuote}
                    className="w-full min-h-10 flex items-center gap-3 px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-left disabled:cursor-default disabled:hover:bg-white"
                  >
                    <FileText className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    <span className="font-mono text-sm font-semibold text-stone-800">{q.quotation_number || `#${q.id}`}</span>
                    <Badge label={q.status} status={q.status} />
                    <span className="ml-auto text-sm text-stone-600 tabular-nums">{formatCurrencyFull(Number(q.total_amount) || 0)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-500">
                No quotation yet.{canQuote && e.status !== "rejected" ? " Use “Create quotation” to price this enquiry; it becomes Converted once you save the quotation." : ""}
              </p>
            )}
            {e.event && (
              <button
                type="button"
                onClick={() => { onClose(); navigate(`/events/detail?id=${e.event!.id}`); }}
                className="mt-2 w-full min-h-10 flex items-center gap-3 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50 text-left"
              >
                <CalendarCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-stone-500">Event</span>
                <span className="text-sm font-semibold text-stone-800 truncate">{e.event.name}</span>
                <span className="ml-auto"><Badge label={e.event.status.replace("_", " ")} status={e.event.status} /></span>
              </button>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
